'use client';

import React, { useState } from 'react';
import { Search, Plus, AlertTriangle, CheckCircle, Upload } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import PaymentEntryForm from './PaymentEntryForm';
import OCRUploadPanel from './OCRUploadPanel';
import StatusBadge from '@/components/ui/StatusBadge';

const PAYMENTS_DATA = [
  {
    id: 'pay-001',
    date: '18/06/2026',
    time: '09:45',
    lotNumber: '300',
    direction: 'received' as const,
    party: 'Senthil Traders',
    method: 'UPI',
    utr: 'UTR423891047',
    amount: 35000,
    status: 'verified' as const,
    proof: true,
    duplicate: false,
  },
  {
    id: 'pay-002',
    date: '18/06/2026',
    time: '11:20',
    lotNumber: '470',
    direction: 'received' as const,
    party: 'Karthik Enterprises',
    method: 'Bank Transfer',
    utr: 'NEFT20240618001',
    amount: 25000,
    status: 'verified' as const,
    proof: true,
    duplicate: false,
  },
  {
    id: 'pay-003',
    date: '18/06/2026',
    time: '14:00',
    lotNumber: '300',
    direction: 'received' as const,
    party: 'Senthil Traders',
    method: 'UPI',
    utr: 'UTR423891047',
    amount: 35000,
    status: 'duplicate' as const,
    proof: true,
    duplicate: true,
  },
  {
    id: 'pay-004',
    date: '17/06/2026',
    time: '16:30',
    lotNumber: '185',
    direction: 'sent' as const,
    party: 'Kumar Brothers',
    method: 'Bank Transfer',
    utr: 'RTGS20240617088',
    amount: 180000,
    status: 'verified' as const,
    proof: true,
    duplicate: false,
  },
  {
    id: 'pay-005',
    date: '17/06/2026',
    time: '10:00',
    lotNumber: '410',
    direction: 'received' as const,
    party: 'Priya Vegetable Store',
    method: 'Cash',
    utr: '',
    amount: 15000,
    status: 'verified' as const,
    proof: false,
    duplicate: false,
  },
  {
    id: 'pay-006',
    date: '16/06/2026',
    time: '15:00',
    lotNumber: '211',
    direction: 'sent' as const,
    party: 'Suresh Farm',
    method: 'Cheque',
    utr: 'CHQ00124567',
    amount: 95000,
    status: 'pending' as const,
    proof: false,
    duplicate: false,
  },
  {
    id: 'pay-007',
    date: '16/06/2026',
    time: '09:00',
    lotNumber: '300',
    direction: 'received' as const,
    party: 'Murugan Wholesale',
    method: 'UPI',
    utr: 'UTR398712345',
    amount: 17500,
    status: 'verified' as const,
    proof: true,
    duplicate: false,
  },
  {
    id: 'pay-008',
    date: '15/06/2026',
    time: '14:45',
    lotNumber: '185',
    direction: 'received' as const,
    party: 'Selvam & Co',
    method: 'UPI',
    utr: 'UTR378451290',
    amount: 23625,
    status: 'verified' as const,
    proof: true,
    duplicate: false,
  },
];

const PAYMENT_SUMMARY = {
  totalReceived: 116125,
  totalSent: 275000,
  pendingReceivable: 129000,
  duplicatesDetected: 1,
};

const METHOD_COLORS: Record<string, string> = {
  UPI: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'Bank Transfer': 'bg-info/10 text-info border-info/20',
  Cash: 'bg-accent/10 text-accent border-accent/20',
  Cheque: 'bg-warning/10 text-warning border-warning/20',
};

export default function PaymentManagementClient() {
  const [search, setSearch] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [directionFilter, setDirectionFilter] = useState('All');

  const filtered = PAYMENTS_DATA.filter((p) => {
    const matchSearch =
      p.party.toLowerCase().includes(search.toLowerCase()) ||
      p.lotNumber.includes(search) ||
      p.utr.toLowerCase().includes(search.toLowerCase());
    const matchDir = directionFilter === 'All' || p.direction === directionFilter.toLowerCase();
    return matchSearch && matchDir;
  });

  return (
    <div className="space-y-4">
      {/* Duplicate Alert Banner */}
      {PAYMENT_SUMMARY.duplicatesDetected > 0 && (
        <div className="flex items-center gap-3 bg-danger/10 border border-danger/30 rounded-xl p-3">
          <AlertTriangle size={16} className="text-danger flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-danger">Duplicate Payment Detected</p>
            <p className="text-xs text-muted-foreground">UTR423891047 appears twice — review and remove the duplicate entry</p>
          </div>
          <button className="text-xs text-danger font-semibold hover:underline">Review</button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Received Today', value: `₹${PAYMENT_SUMMARY.totalReceived.toLocaleString('en-IN')}`, color: 'text-accent', bg: 'bg-accent/5 border-accent/20' },
          { label: 'Sent Today', value: `₹${PAYMENT_SUMMARY.totalSent.toLocaleString('en-IN')}`, color: 'text-info', bg: 'bg-info/5 border-info/20' },
          { label: 'Pending Receivable', value: `₹${PAYMENT_SUMMARY.pendingReceivable.toLocaleString('en-IN')}`, color: 'text-warning', bg: 'bg-warning/5 border-warning/20' },
          { label: 'Duplicates', value: PAYMENT_SUMMARY.duplicatesDetected.toString(), color: 'text-danger', bg: 'bg-danger/5 border-danger/20' },
        ].map((s) => (
          <div key={`pay-summary-${s.label}`} className={`card-base p-3 border ${s.bg}`}>
            <p className={`text-xl font-bold amount-display ${s.color}`}>{s.value}</p>
            <p className="text-2xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by party, lot, UTR..."
            className="input-field pl-9"
          />
        </div>
        <div className="flex gap-2">
          {['All', 'Received', 'Sent'].map((f) => (
            <button
              key={`dir-filter-${f}`}
              onClick={() => setDirectionFilter(f)}
              className={`text-xs font-medium px-3 py-2 rounded-lg border transition-all ${
                directionFilter === f
                  ? 'bg-primary/10 text-primary border-primary/30' :'bg-muted text-muted-foreground border-border hover:border-primary/30'
              }`}
            >
              {f}
            </button>
          ))}
          <button
            onClick={() => setShowOCRModal(true)}
            className="btn-secondary text-sm py-2"
          >
            <Upload size={14} />
            OCR Upload
          </button>
          <button
            onClick={() => setShowPaymentModal(true)}
            className="btn-primary text-sm py-2"
          >
            <Plus size={14} />
            Add Payment
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card-base overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="table-header-cell">Date / Time</th>
                <th className="table-header-cell">Lot #</th>
                <th className="table-header-cell">Direction</th>
                <th className="table-header-cell">Party</th>
                <th className="table-header-cell">Method</th>
                <th className="table-header-cell">UTR / Reference</th>
                <th className="table-header-cell">Amount</th>
                <th className="table-header-cell">Proof</th>
                <th className="table-header-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((pay) => (
                <tr key={pay.id} className={`table-row ${pay.duplicate ? 'bg-danger/5' : ''}`}>
                  <td className="table-cell">
                    <p className="text-xs font-medium">{pay.date}</p>
                    <p className="text-2xs text-muted-foreground">{pay.time}</p>
                  </td>
                  <td className="table-cell">
                    <span className="lot-number font-bold text-primary">#{pay.lotNumber}</span>
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-bold border ${
                      pay.direction === 'received' ?'bg-accent/10 text-accent border-accent/20' :'bg-info/10 text-info border-info/20'
                    }`}>
                      {pay.direction === 'received' ? '↓ IN' : '↑ OUT'}
                    </span>
                  </td>
                  <td className="table-cell font-medium">{pay.party}</td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold border ${METHOD_COLORS[pay.method] || 'bg-muted text-muted-foreground border-border'}`}>
                      {pay.method}
                    </span>
                  </td>
                  <td className="table-cell">
                    {pay.utr ? (
                      <div className="flex items-center gap-1.5">
                        <span className="lot-number text-xs text-foreground">{pay.utr}</span>
                        {pay.duplicate && (
                          <AlertTriangle size={11} className="text-danger" />
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <span className={`font-bold amount-display ${pay.direction === 'received' ? 'text-accent' : 'text-info'}`}>
                      {pay.direction === 'received' ? '+' : '-'}₹{pay.amount.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="table-cell">
                    {pay.proof ? (
                      <span className="flex items-center gap-1 text-accent text-xs">
                        <CheckCircle size={12} />
                        Uploaded
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="table-cell">
                    {pay.duplicate ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-danger/10 text-danger border border-danger/20">
                        <AlertTriangle size={10} />
                        Duplicate
                      </span>
                    ) : (
                      <StatusBadge status={pay.status === 'verified' ? 'settled' : 'pending'} label={pay.status === 'verified' ? 'Verified' : 'Pending'} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {PAYMENTS_DATA.length} payments
          </p>
          <div className="flex items-center gap-1">
            {[1, 2].map((page) => (
              <button
                key={`pay-page-${page}`}
                className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                  page === 1
                    ? 'bg-primary/10 text-primary border border-primary/30' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Payment Entry Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Record Payment"
        size="lg"
      >
        <PaymentEntryForm onClose={() => setShowPaymentModal(false)} />
      </Modal>

      {/* OCR Upload Modal */}
      <Modal
        isOpen={showOCRModal}
        onClose={() => setShowOCRModal(false)}
        title="Upload Payment Screenshot — OCR"
        size="lg"
      >
        <OCRUploadPanel onClose={() => setShowOCRModal(false)} />
      </Modal>
    </div>
  );
}